import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import "resize-observer-polyfill";

import { useStoreContext } from "../../contexts/StoreDataContext";
import useCollection from "../../hooks/useCollection";
import useStoreProducts from "../../hooks/useStoreProducts";
import Error500Page from "../../pages/500";
import CartStorage from "../../services/frontend/CartStorage";
import Basket from "../Cart/Basket";
import EmptyStore from "../EmptyStore";
import NavBar from "../Navbar";
import SideNavDrawer from "../SideNavDrawer";
import { Skeleton } from "../Skeleton";
import SkeletonCard from "../SkeletonCard";
import StoreCollections from "../StoreCollections";
import StoreItem from "../StoreItem";
import RestorableList from "./RestorableList";

export const ALL_PRODUCTS_OBJECT = {
  name: "all products",
  _id: "all",
};

const CollectionHome = ({ store, theme, hasBanner }) => {
  const router = useRouter();
  const queryClient = useQueryClient();

  const context = useStoreContext();
  const isProplanActive = context.isProplanActive;
  const hasBannerWithProPlan = isProplanActive && hasBanner;

  const [selectedCollection, setSelectedCollection] = useState(ALL_PRODUCTS_OBJECT);
  const [lastEl, setLastEl] = useState(null);
  const { ref, inView } = useInView({
    threshold: 0,
  });

  const {
    data: productData,
    isLoading: isProductsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    error: productError,
  } = useStoreProducts(store.id, selectedCollection?._id);

  const {
    data: collectionsData,
    isLoading: isCollectionsLoading,
    error: collectionsError,
  } = useCollection(store.id, "");

  // Assuming collectionsData is now structured as { collections: [...] }
  const collections = collectionsData ? collectionsData.collections : [];
  //console.log("shubham", collectionsData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const products = productData?.pages.flatMap((page) => page.products) || [];
  const [cart, setCart] = useState(CartStorage.getInitialCart());
  const [showCart, setShowCart] = useState(false);
  const [showSideNav, setShowSideNav] = useState(false);
  const [isCartInitialized, setIsCartInitialized] = useState(false);

  // Initialize cart from CartStorage when component mounts
  useEffect(() => {
    const initialize = async () => {
      const initialCartData = await CartStorage.initializeCart();
      setCart(initialCartData);
      setIsCartInitialized(true);
    };
    initialize();
  }, []);

  // Update CartStorage when cart changes
  useEffect(() => {
    if (cart !== CartStorage.getInitialCart()) {
      CartStorage.saveCart(cart);
    }
  }, [cart]);

  const handleShowCart = useCallback(
    (boolVal = false) => {
      if (isCollectionsLoading || collectionsError || productError) return;
      setShowCart(boolVal);
    },
    [isCollectionsLoading, collectionsError, productError],
  );

  useEffect(() => {
    const { collection: queryCollection } = router.query;
    if (queryCollection) {
      const foundCollection = collections.find((c) => {
        return c.name.trim().toLowerCase() === queryCollection.trim().toLowerCase();
      });
      setSelectedCollection(foundCollection);
    } else {
      setSelectedCollection(ALL_PRODUCTS_OBJECT);
    }
  }, [collections, router.query]);

  const setFilter = useCallback(
    (collection) => {
      queryClient.invalidateQueries(["storeProducts", store.id, collection]);
      queryClient.removeQueries(["storeProducts", store.id, selectedCollection]);
      setSelectedCollection(collection);
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            collection: collection.name === "all products" ? "" : collection.name,
          },
        },
        undefined,
        { shallow: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router],
  );

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    if (!products.length) return;
    const [lastElement] = products.slice(-1);
    setLastEl(lastElement.id);
  }, [products]);

  const updateAndSaveCart = async (newCartData) => {
    setCart(newCartData);
    await CartStorage.saveCart(newCartData);
  };

  const updateAddedToCart = (productId, value) => {
    console.log(
      `Product ${productId} added status: ${value} (update logic needed here if applicable)`,
    );
  };

  if (collectionsError || productError) return <Error500Page />;

  return (
    <div
      className={`scrollbar-none ${hasBannerWithProPlan ? "mt-0" : "mt-[38px]"} min-h-[calc(100vh-268px)] w-full px-[22px]`}
    >
      {isCartInitialized && cart?.products?.length > 0 && (
        <Basket
          isBasketOpen={showCart}
          setCart={updateAndSaveCart}
          cartData={cart}
          updateAddedToCart={updateAddedToCart}
          handleShowCart={handleShowCart}
          store={store}
          setRefresh={() => {}}
          isProplanActive={isProplanActive}
        />
      )}

      <NavBar
        cartActive={cart.products.length > 0}
        handleShowCart={handleShowCart}
        hideInAdvance={false}
        homeActive={hasBannerWithProPlan}
        store={store}
        theme={theme}
        storeName={store?.storeName || ""}
        toggleSideNav={() => setShowSideNav(!showSideNav)}
        homePage={hasBannerWithProPlan}
      />

      {isProplanActive && (
        <SideNavDrawer
          isOpen={showSideNav}
          toggleDrawer={() => setShowSideNav(!showSideNav)}
          store={store}
        />
      )}

      <div
        className={
          isCollectionsLoading || (collections && collections.length > 0)
            ? `hide-scrollbar relative left-0 right-0 ${hasBannerWithProPlan ? "top-0" : "top-14"} z-10 ${hasBannerWithProPlan ? "mt-[18px]" : "mt-[5px]"} flex h-12 max-w-[500px] overflow-x-auto overflow-y-hidden scroll-smooth px-0 leading-8`
            : ""
        }
      >
        {collections && collections.length > 0 ? (
          <StoreCollections
            theme={theme}
            collections={collections}
            setFilter={setFilter}
            selectedCollection={selectedCollection}
          />
        ) : (
          <div className="h-5"></div>
        )}
        {isCollectionsLoading &&
          Array(3)
            .fill(0)
            .map((_, i) => (
              <Skeleton
                className="m-[4px_5px_5px_4px] w-[150px] rounded-[50px] px-5 py-[4px]"
                key={i}
              />
            ))}
      </div>
      <div
        className={`relative ${hasBannerWithProPlan ? "mt-6" : "mt-4 pt-16"} grid min-h-[80vh] gap-[11px_22px]`}
      >
        {products.length > 0 ? (
          <RestorableList>
            {React.Children.toArray(
              products.map((_, index) => {
                const startIndex = index * 2;
                const endIndex = startIndex + 2;
                const rowProducts = products.slice(startIndex, endIndex);
                if (!rowProducts.length) return null;
                return (
                  <div className="grid grid-cols-2 gap-4">
                    {React.Children.toArray(
                      rowProducts.map((product) => (
                        <div ref={lastEl === product.id ? ref : null}>
                          <StoreItem
                            cohort={store.cohort.includes(2) ? store.cohort : null}
                            product={product}
                            top={index === 0}
                          />
                        </div>
                      )),
                    )}
                  </div>
                );
              }),
            )}
          </RestorableList>
        ) : (
          !isProductsLoading && <EmptyStore store={store} />
        )}
        {/* Loader for next page */}
        {(isFetchingNextPage || isProductsLoading) && (
          <div className="grid grid-cols-2 gap-4">
            {Array(6)
              .fill(0)
              .map((_, i) => (
                <div key={i}>
                  <SkeletonCard
                    imageClass="rounded-lg object-cover max-w-[none] w-[calc((100vw/2)-33px)] h-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)] md:h-[calc((520px/2)-33px)]"
                    titleClass="h-4 w-[calc((100vw/2)-33px)] md:w-[calc((520px/2)-33px)]"
                    descriptionClass="h-4 w-[calc((100vw/2)-150px)] md:w-[calc((520px/2)-150px)]"
                    containerClass="flex justify-start items-start flex-col max-w-[calc((100vw/2)-33px)] mb-8 md:max-w-[calc((520px/2)-33px)]"
                    key={i}
                  />
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHome;
